import { getContactSelectQueryKey } from '@/components/Form/Shared/ContactSelect'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    CONTACT_LABELS,
    CONTACT_ROLE_OPTIONS,
    CUSTOMER_SOURCES,
} from '@/modules/Customer/constants'
import {
    createContact,
    createKeycloakAccount,
} from '@/modules/Customer/services'
import { CustomerContact } from '@/modules/Customer/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { Copy, Dices, Eye, EyeOff, PenLine } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Checkbox,
    Dialog,
    GridHookForm,
    TextField,
    Typogaphy,
} from 'vnetwork-ui'
import { schemaAddNewContact } from './schema'

// Generate a random password with uppercase, lowercase, numbers
const generatePassword = (length = 12): string => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const lowercase = 'abcdefghijklmnopqrstuvwxyz'
    const numbers = '0123456789'
    const allChars = uppercase + lowercase + numbers

    // Ensure at least one of each type, starting with uppercase
    let password = uppercase[Math.floor(Math.random() * uppercase.length)]
    password += lowercase[Math.floor(Math.random() * lowercase.length)]
    password += numbers[Math.floor(Math.random() * numbers.length)]

    // Fill the rest randomly
    for (let i = 3; i < length; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)]
    }

    // Shuffle but keep first char as uppercase
    const firstChar = password[0]
    const rest = password
        .slice(1)
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('')
    return firstChar + rest
}

interface AddNewContactFormValues extends Partial<CustomerContact> {
    enable_authentication?: boolean
    password?: string
    password_confirmation?: string
    send_welcome_email?: boolean
}

export interface AddNewContactProps extends ModalProps {
    customer_id: number
    company_id: number
}

const AddNewContact = ({
    onClose,
    open,
    customer_id,
    company_id,
}: AddNewContactProps) => {
    const { t } = useTranslation()
    const { t: tCustomer } = useTranslation('customer')
    const queryClient = useQueryClient()
    const [enableAuth, setEnableAuth] = useState(false)
    const [useAutoPassword, setUseAutoPassword] = useState(true)
    const [generatedPassword, setGeneratedPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const formControl = useForm<AddNewContactFormValues>({
        defaultValues: {
            contact_label: ['contact-first'],
            contact_roles: ['customer'],
            contact_name: '',
            contact_email: '',
            contact_phone: '',
            contact_position: '',
            contact_address: '',
            contact_source: '',
            company_id,
            enable_authentication: false,
            password: '',
            password_confirmation: '',
            send_welcome_email: true,
        },
        resolver: yupResolver(schemaAddNewContact) as any,
    })

    const { mutateAsync } = useMutationWithHandler(
        { mutationFn: createContact },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_CONTACTS,
            ],
            showSuccessToast: false,
        }
    )

    const handleCreate = async (values: AddNewContactFormValues) => {
        const {
            enable_authentication,
            password,
            password_confirmation,
            send_welcome_email,
            ...contactPayload
        } = values

        setIsSubmitting(true)
        try {
            const result = await mutateAsync({
                contact_payload: contactPayload,
                customer_id,
            })

            const contactId = result?.result?.customer_contact_id

            if (enable_authentication && contactId && password) {
                try {
                    await createKeycloakAccount({
                        contact_id: contactId,
                        password,
                        password_confirmation:
                            password_confirmation || password,
                        send_welcome_email: send_welcome_email ?? true,
                    })
                    toast.success(t('add-new-contact-with-account-success'))
                } catch {
                    toast.success(t('add-new-contact-success'))
                    toast.error(
                        tCustomer('contact.failed-to-create-keycloak-account')
                    )
                }
            } else {
                toast.success(t('add-new-contact-success'))
            }

            // Invalidate contact select dropdown
            queryClient.invalidateQueries({
                queryKey: getContactSelectQueryKey(customer_id),
            })

            onClose()
        } catch {
            // Error handled by mutation handler
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleGeneratePassword = useCallback(() => {
        const newPassword = generatePassword(12)
        setGeneratedPassword(newPassword)
        formControl.setValue('password', newPassword)
        formControl.setValue('password_confirmation', newPassword)
        formControl.clearErrors(['password', 'password_confirmation'])
    }, [formControl])

    const handleCopyPassword = useCallback(() => {
        if (generatedPassword) {
            navigator.clipboard.writeText(generatedPassword)
            toast.success(t('password-copied'))
        }
    }, [generatedPassword, t])

    const handleEnableAuthChange = (checked: boolean) => {
        setEnableAuth(checked)
        formControl.setValue('enable_authentication', checked)
        if (checked) {
            // Auto-generate password when enabling auth
            setUseAutoPassword(true)
            handleGeneratePassword()
        } else {
            formControl.setValue('password', '')
            formControl.setValue('password_confirmation', '')
            formControl.clearErrors(['password', 'password_confirmation'])
            setGeneratedPassword('')
            setUseAutoPassword(true)
            setShowPassword(false)
        }
    }

    const handleTogglePasswordMode = (useAuto: boolean) => {
        setUseAutoPassword(useAuto)
        if (useAuto) {
            handleGeneratePassword()
        } else {
            formControl.setValue('password', '')
            formControl.setValue('password_confirmation', '')
            setGeneratedPassword('')
        }
    }

    return (
        <Dialog title={t('add-new-contact')} onClose={onClose} open={open}>
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isSubmitting,
                    },
                    {
                        title: t('add-new-contact'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isSubmitting,
                        disabled: isSubmitting,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'contact_label',
                            type: 'select',
                            label: t('contact_label'),
                            placeholder: t('contact_label'),
                            options: CONTACT_LABELS(t),
                            multiple: true,
                            allowClear: false,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contact_roles',
                            type: 'select',
                            label: tCustomer('contact_roles.label'),
                            placeholder: tCustomer('contact_roles.label'),
                            options: CONTACT_ROLE_OPTIONS(tCustomer),
                            multiple: true,
                            allowClear: false,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contact_name',
                            type: 'textfield',
                            label: t('contact_name'),
                            placeholder: t('contact_name'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contact_email',
                            type: 'textfield',
                            label: 'Email',
                            placeholder: 'Email',
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_phone',
                            type: 'textfield',
                            label: t('employee_phone_number'),
                            placeholder: t('employee_phone_number'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_position',
                            type: 'textfield',
                            label: t('position_id'),
                            placeholder: t('position_id'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_source',
                            type: 'select',
                            label: t('customer_source'),
                            placeholder: t('customer_source'),
                            options: CUSTOMER_SOURCES(t)?.filter(
                                (item) => item.value !== 'marketing_approach'
                            ),
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contact_address',
                            type: 'textarea',
                            label: t('address'),
                            placeholder: t('address'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        renderEditComponent: () => (
                            <div className="col-span-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Controller
                                        name="enable_authentication"
                                        control={formControl.control}
                                        render={({ field }) => (
                                            <Checkbox
                                                checked={field.value ?? false}
                                                onChange={(e) => {
                                                    handleEnableAuthChange(
                                                        e.target.checked
                                                    )
                                                }}
                                            />
                                        )}
                                    />
                                    <Typogaphy className="font-medium">
                                        {t('enable-authentication')}
                                    </Typogaphy>
                                </div>
                                {enableAuth && (
                                    <Typogaphy
                                        size="small"
                                        className="text-gray-500 mb-3"
                                    >
                                        {t('enable-authentication-description')}
                                    </Typogaphy>
                                )}
                            </div>
                        ),
                        className: 'col-span-4',
                    },
                    ...(enableAuth
                        ? [
                              {
                                  renderEditComponent: () => (
                                      <div className="col-span-4">
                                          {/* Toggle buttons for password mode */}
                                          <div className="flex gap-2 mb-3">
                                              <Button
                                                  type="button"
                                                  size="small"
                                                  variant={
                                                      useAutoPassword
                                                          ? 'contained'
                                                          : 'outlined'
                                                  }
                                                  onClick={() =>
                                                      handleTogglePasswordMode(
                                                          true
                                                      )
                                                  }
                                              >
                                                  <Dices className="h-4 w-4 mr-1" />
                                                  {t('auto-generate-password')}
                                              </Button>
                                              <Button
                                                  type="button"
                                                  size="small"
                                                  variant={
                                                      !useAutoPassword
                                                          ? 'contained'
                                                          : 'outlined'
                                                  }
                                                  onClick={() =>
                                                      handleTogglePasswordMode(
                                                          false
                                                      )
                                                  }
                                              >
                                                  <PenLine className="h-4 w-4 mr-1" />
                                                  {t('set-custom-password')}
                                              </Button>
                                          </div>

                                          {useAutoPassword ? (
                                              /* Auto-generated password display */
                                              <div className="flex items-center gap-2">
                                                  <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md border">
                                                      <code className="flex-1 font-mono text-sm">
                                                          {showPassword
                                                              ? generatedPassword
                                                              : '••••••••••••'}
                                                      </code>
                                                      <button
                                                          type="button"
                                                          onClick={() =>
                                                              setShowPassword(
                                                                  !showPassword
                                                              )
                                                          }
                                                          className="p-1 hover:bg-gray-200 rounded"
                                                      >
                                                          {showPassword ? (
                                                              <EyeOff className="h-4 w-4 text-gray-500" />
                                                          ) : (
                                                              <Eye className="h-4 w-4 text-gray-500" />
                                                          )}
                                                      </button>
                                                  </div>
                                                  <Button
                                                      type="button"
                                                      size="small"
                                                      variant="outlined"
                                                      onClick={
                                                          handleGeneratePassword
                                                      }
                                                      title={t(
                                                          'regenerate-password'
                                                      )}
                                                  >
                                                      <Dices className="h-4 w-4" />
                                                  </Button>
                                                  <Button
                                                      type="button"
                                                      size="small"
                                                      variant="outlined"
                                                      onClick={
                                                          handleCopyPassword
                                                      }
                                                      title={t('copy-password')}
                                                  >
                                                      <Copy className="h-4 w-4" />
                                                  </Button>
                                              </div>
                                          ) : (
                                              /* Manual password entry */
                                              <div className="grid grid-cols-2 gap-4">
                                                  <Controller
                                                      name="password"
                                                      control={
                                                          formControl.control
                                                      }
                                                      render={({
                                                          field,
                                                          fieldState,
                                                      }) => (
                                                          <TextField
                                                              {...field}
                                                              type="password"
                                                              label={t(
                                                                  'password'
                                                              )}
                                                              placeholder={t(
                                                                  'password'
                                                              )}
                                                              size="small"
                                                              error={
                                                                  !!fieldState.error
                                                              }
                                                              helperText={
                                                                  fieldState
                                                                      .error
                                                                      ?.message
                                                              }
                                                          />
                                                      )}
                                                  />
                                                  <Controller
                                                      name="password_confirmation"
                                                      control={
                                                          formControl.control
                                                      }
                                                      render={({
                                                          field,
                                                          fieldState,
                                                      }) => (
                                                          <TextField
                                                              {...field}
                                                              type="password"
                                                              label={tCustomer(
                                                                  'contact.confirm-password'
                                                              )}
                                                              placeholder={tCustomer(
                                                                  'contact.confirm-password'
                                                              )}
                                                              size="small"
                                                              error={
                                                                  !!fieldState.error
                                                              }
                                                              helperText={
                                                                  fieldState
                                                                      .error
                                                                      ?.message
                                                              }
                                                          />
                                                      )}
                                                  />
                                              </div>
                                          )}
                                      </div>
                                  ),
                                  className: 'col-span-4',
                              },
                              {
                                  renderEditComponent: () => (
                                      <div className="flex items-center gap-2">
                                          <Controller
                                              name="send_welcome_email"
                                              control={formControl.control}
                                              render={({ field }) => (
                                                  <Checkbox
                                                      checked={
                                                          field.value ?? true
                                                      }
                                                      onChange={(e) =>
                                                          field.onChange(
                                                              e.target.checked
                                                          )
                                                      }
                                                  />
                                              )}
                                          />
                                          <Typogaphy size="small">
                                              {tCustomer(
                                                  'contact.send-welcome-email'
                                              )}
                                          </Typogaphy>
                                      </div>
                                  ),
                                  className: 'col-span-4',
                              },
                          ]
                        : []),
                ]}
            />
        </Dialog>
    )
}

export default AddNewContact
